import React, { useEffect, useRef, useState } from 'react';
import { churchAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { QrCode, Copy, Download, FileText, Loader2, RefreshCw, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

export const InviteQrCard = () => {
  const { selectedChurchId, isSuperAdmin } = useAuth();
  const [link, setLink] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [error, setError] = useState(null);
  const previewRef = useRef(null);

  const load = () => {
    if (!selectedChurchId) return;
    setLoading(true);
    setError(null);
    churchAPI.inviteLink(selectedChurchId)
      .then(res => {
        setLink(res.data?.data || null);
        return churchAPI.inviteQr(selectedChurchId, 'png');
      })
      .then(res => {
        if (previewRef.current) URL.revokeObjectURL(previewRef.current);
        previewRef.current = URL.createObjectURL(res.data);
        setPreview(previewRef.current);
      })
      .catch(err => setError(err?.response?.data?.detail || 'No se pudo obtener el enlace de invitación'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    return () => { if (previewRef.current) URL.revokeObjectURL(previewRef.current); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedChurchId]);

  const download = async (format) => {
    setDownloading(format);
    try {
      const res = await churchAPI.inviteQr(selectedChurchId, format);
      const url = URL.createObjectURL(res.data);
      const suggested = /filename="([^"]+)"/.exec(res.headers?.['content-disposition'] || '');
      const a = document.createElement('a');
      a.href = url;
      a.download = suggested ? suggested[1] : `qr-invitacion.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(format === 'pdf' ? 'PDF listo para imprimir' : 'Imagen descargada');
    } catch {
      toast.error('No se pudo generar el archivo');
    } finally {
      setDownloading(null);
    }
  };

  const rotate = async () => {
    setRotating(true);
    try {
      await churchAPI.rotateInviteLink(selectedChurchId);
      toast.success('Enlace regenerado: vuelve a imprimir el QR');
      setConfirmRotate(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo regenerar el enlace');
    } finally {
      setRotating(false);
    }
  };

  const shareText = link
    ? `Únete a nuestra congregación en Tu Iglesia App: ${link.url}`
    : '';

  if (!selectedChurchId) return null;

  return (
    <Card data-testid="settings-invite-qr-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="w-5 h-5" /> Código QR de invitación
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Quien escanee este código entra a Tu Iglesia App con tu congregación ya seleccionada.
          Imprímelo en el boletín, pégalo en la cartelera o proyéctalo al final del culto.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-7 h-7 spinner text-[#056A84]" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3" data-testid="invite-qr-error">
            <p className="text-sm text-yellow-800">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={load} data-testid="invite-qr-retry">
              <RefreshCw className="w-4 h-4 mr-1" /> Reintentar
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
              {preview && (
                <img
                  src={preview}
                  alt="Código QR de invitación"
                  className="w-56 rounded-xl border bg-white"
                  data-testid="invite-qr-preview"
                />
              )}
              <div className="flex-1 w-full space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Enlace de invitación
                  </Label>
                  <div className="flex gap-2">
                    <Input readOnly value={link?.url || ''} className="font-mono text-xs" data-testid="invite-qr-url" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { navigator.clipboard.writeText(link?.url || ''); toast.success('Enlace copiado'); }}
                      aria-label="Copiar enlace de invitación"
                      title="Copiar enlace"
                      data-testid="invite-qr-copy"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  {link?.code && (
                    <p className="text-xs text-muted-foreground" data-testid="invite-qr-code">
                      Código corto: <span className="font-mono font-semibold text-foreground">{link.code}</span>
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift"
                    onClick={() => download('png')}
                    disabled={downloading === 'png'}
                    data-testid="invite-qr-download-png"
                  >
                    {downloading === 'png' ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                    Descargar PNG
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => download('pdf')}
                    disabled={downloading === 'pdf'}
                    data-testid="invite-qr-download-pdf"
                  >
                    {downloading === 'pdf' ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
                    PDF para imprimir
                  </Button>
                  <Button variant="outline" asChild data-testid="invite-qr-whatsapp">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" /> Compartir por WhatsApp
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {isSuperAdmin && (
              <div className="pt-1 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive mt-3"
                  onClick={() => setConfirmRotate(true)}
                  data-testid="invite-qr-rotate"
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Regenerar enlace
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent data-testid="invite-qr-rotate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Regenerar el enlace de invitación?</AlertDialogTitle>
            <AlertDialogDescription>
              Se emitirá un código nuevo y el anterior dejará de funcionar: todos los QR ya
              impresos o compartidos quedarán inservibles. Hazlo solo si el enlace se filtró.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invite-qr-rotate-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => { e.preventDefault(); rotate(); }}
              disabled={rotating}
              data-testid="invite-qr-rotate-confirm"
            >
              {rotating ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}
              Sí, regenerar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
